import { FormControl, FormLabel, Grid, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';

import { DatePicker } from '@components/datePicker';
import { FieldComponentProps } from '@components/forms/FieldComponent';
import { TimePicker } from '@components/timePicker/TimePicker';
import { useFormStore } from '@components/forms/Form';
import { isSameDay, isValid } from 'date-fns';
import { compareTimes } from '@utils/compareTimes';

/** future considerations
 * 1. could break up each date + time picker into its own "inner" field component
 * 2. Right now this has to be in the formstore, we may want to expand and then wrap in controller to use this outside of here in the future
 */
export const DateTimeRangeField = ({ field, forceDisabled }: FieldComponentProps) => {
  const { control, formState, watch } = useFormStore((state) => state);

  const dateTimeRangeFieldConfig = field.fieldConfig?.dateTimeRangeField;
  const config = dateTimeRangeFieldConfig!;

  const minStartDate: Date | undefined = watch(config.startDate.name) as any;
  const startTime = watch(config.startTime.name) as any;
  const endDate: Date | undefined = watch(config.endDate.name) as any;

  return (
    <Grid
      container
      size={{ xs: 12 }}
      sx={{
        padding: 0,
        alignItems: 'flex-start',
      }}
    >
      <Grid>
        <FormControl>
          <FormLabel>{field.label}</FormLabel>
          <Grid
            container
            sx={{
              flexDirection: 'row',
              gap: 1,
            }}
          >
            <Grid>
              <Controller
                rules={{ required: field.required as boolean }}
                defaultValue={field.defaultValue}
                name={config.startDate.name}
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <DatePicker
                      value={isValid(value) ? value : null}
                      onChange={onChange}
                      disabled={(field.disabled as boolean) || forceDisabled}
                      minDate={config.startDate?.minDate}
                      textFieldProps={{
                        error: !!formState.errors[field.name]?.message,
                      }}
                    />
                  );
                }}
              />
            </Grid>
            <Grid sx={{ width: '150px' }}>
              <Controller
                rules={{ required: config.startTime?.required }}
                defaultValue={config.startTime?.default}
                name={config.startTime.name}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TimePicker
                    value={value}
                    onChange={onChange}
                    disabled={config.startTime?.disabled || forceDisabled}
                    textFieldProps={{
                      placeholder: 'Start Time',
                      error: !!formState.errors[config.startTime.name]?.message,
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </FormControl>
      </Grid>
      <Grid
        sx={{
          mt: '26px',
          ml: 2,
          mr: 2,
        }}
      >
        <Typography variant='bodySmall'>To</Typography>
      </Grid>
      <FormControl>
        <FormLabel>{config.endDate.label}</FormLabel>
        <Grid
          container
          sx={{
            flexDirection: 'row',
            gap: 1,
            alignItems: 'flex-start',
          }}
        >
          <Grid>
            <Controller
              rules={{ required: config.startTime?.required }}
              name={config.endDate?.name}
              defaultValue={config.endDate?.default}
              control={control}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  value={value}
                  onChange={onChange}
                  disabled={config.endDate.disabled || forceDisabled}
                  minDate={minStartDate}
                  textFieldProps={{
                    error: !!formState.errors[config.endDate.name]?.message,
                  }}
                />
              )}
            />
          </Grid>
          <Grid sx={{ width: '150px' }}>
            <Controller
              rules={{
                required: config.startTime?.required,
                validate: (value) => {
                  if (!(minStartDate && endDate && isSameDay(minStartDate, endDate))) {
                    return true;
                  }

                  if (compareTimes(value, startTime)) {
                    return 'Invalid Time';
                  }

                  return isValid(value);
                },
              }}
              defaultValue={config.endTime.default}
              name={config.endTime.name}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TimePicker
                  value={value}
                  minTime={minStartDate && endDate && isSameDay(minStartDate, endDate) ? minStartDate : undefined}
                  onChange={onChange}
                  disabled={config.endTime.disabled || forceDisabled}
                  textFieldProps={{
                    placeholder: 'End Time',
                    error: !!formState.errors[config.endTime.name]?.message,
                    helperText: formState.errors[config.endTime.name]?.message,
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </FormControl>
    </Grid>
  );
};
